/** Medal criteria (02 §11). One award of each medal per ranger; at most one of each per mission. */

import type { ArchetypeId } from '../../sim/types';
import { ESCORT_MAX, MEDAL_RULES, MEDALS } from '../content/ranks';
import type { FullSummary } from '../state';
import type { MedalId, RangerRecord } from '../types';

export interface MedalContext {
  ranger: RangerRecord;
  summary: FullSummary;
  archetype: ArchetypeId;
  /** Medal of Honor is Operation-only (the Classic Campaign has no API yet). */
  mode: 'operation' | 'classic';
  /** Captured rangers this mission brought home. */
  freed: number;
  /** Career landings on the map marker, this one included. */
  landings: number;
}

/** Ghost bonus, the span under the convoy, every escort alive, or every partial unit destroyed. */
export function silverStarDeed(summary: FullSummary, archetype: ArchetypeId): boolean {
  if (summary.maxAlert <= MEDAL_RULES.ghostMaxAlert) return true;
  // ROUND4: mission 13's own deed - the piers blown while the convoy was on the deck (10 §2).
  if (summary.spanDeed === true) return true;
  const total = summary.captivesTotal ?? ESCORT_MAX[archetype];
  if (total !== undefined && total > 0 && summary.captivesSaved >= total) return true;
  return summary.allPartials === true;
}

/** Give `medal` if not already held; true when it was new. A top-tier medal marks him Decorated (not in Classic). */
export function giveMedal(ranger: RangerRecord, medal: MedalId, traits = true): boolean {
  if (ranger.medals.includes(medal)) return false;
  ranger.medals.push(medal);
  const topTier = MEDALS.find((m) => m.id === medal)?.topTier === true;
  if (traits && topTier && !ranger.traits.includes('decorated')) ranger.traits.push('decorated');
  return true;
}

/** Award what this mission earned; returns the new medals. */
export function awardMedals(ctx: MedalContext): MedalId[] {
  const { ranger, summary: s, archetype } = ctx;
  const success = s.outcome === 'extracted' && s.objectiveComplete;
  const hadFirst = ranger.medals.includes('commendation-1');
  const deed = silverStarDeed(s, archetype);
  const earned: MedalId[] = [];
  const award = (medal: MedalId, met: boolean): void => {
    if (met && giveMedal(ranger, medal, ctx.mode === 'operation')) earned.push(medal);
  };

  const commended = success && s.score >= MEDAL_RULES.commendationScore;
  award('commendation-1', commended);
  award('commendation-2', commended && hadFirst && s.threat >= MEDAL_RULES.secondCommendationThreat);
  award('bronze-star', success && s.threat >= MEDAL_RULES.bronzeThreat && s.score >= MEDAL_RULES.bronzeScore);
  award('silver-star', success && s.threat >= MEDAL_RULES.silverThreat && deed);
  // R6: the rescue limb carried no outcome of its own. It agreed with "a rescue that frees two or
  // more captured rangers" only because `app/summary.ts` sets `freedPows` on a completed extraction
  // - a chain in another file. The condition the card states is now stated here.
  award('dsc', success && ((s.threat >= MEDAL_RULES.dscThreat && s.woundsAtEnd >= MEDAL_RULES.dscWounds)
    || ctx.freed >= MEDAL_RULES.dscFreed));
  const posthumous = s.outcome === 'kia' && s.objectiveComplete;
  const honorDeed = s.threat >= MEDAL_RULES.honorThreat && deed && (success || posthumous);
  award('medal-of-honor', ctx.mode === 'operation' && honorDeed);
  // R6: "wounded in action" is the wound, not what is left of it at the pickup. A man hit twice who
  // spends a first-aid kit and walks out whole used to get nothing (`woundedInAction`, app/summary.ts).
  award('purple-heart',
    s.outcome === 'kia' || s.woundsAtEnd >= 1 || s.wasDowned || s.woundedInAction === true);
  award('pathfinder-badge', ctx.landings >= MEDAL_RULES.pathfinderLandings);
  return earned;
}
